import { logAmplitudeEvent } from 'amplitude/amplitude'
import { orgSelectors } from 'ducks/orgs'
import { useNotify } from 'ra-core'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import restClient from 'restClient'

export interface ProductAffinityParams {
  orgId?: number
  productCode: string
  distributors?: string[]
  inStock?: boolean
  limit?: number
  timeframeDays?: number
}

export interface AffinityProduct {
  id: number
  code: string
  title: string
  manufacturer: string
  rating: number
  type: string
  affinity_score: number
  co_design_count: number
  co_purchase_count: number
  ordering_v2: any // TODO: Type this properly from existing ordering_v2 types
}

export interface ProductAffinityResponse {
  recommended_products: AffinityProduct[]
  metadata: {
    org_id: number
    role_id: number | null
    product_code: string
    timeframe_days: number
    total_co_designs: number
    total_co_purchases: number
    cache_hit: boolean
  }
}

const restClientInstance = restClient(window.API_ROOT + '/api')

/**
 * Hook to fetch product affinity recommendations for PDP "You May Also Like" section.
 *
 * Uses co-design and co-purchase patterns to find products that are frequently
 * used together with the given product.
 *
 * Algorithm: affinity_score = (co_design_count × 2.0) + (co_purchase_count × 3.0)
 *
 * @param params - Query parameters for affinity recommendations
 * @returns Hook result with affinity recommendations
 */
export const useProductAffinity = (params: ProductAffinityParams) => {
  const [data, setData] = useState<ProductAffinityResponse | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const orgId = useSelector(orgSelectors.getOrg)?.id || params.orgId || 0
  const notify = useNotify()

  // Create stable key from distributors array to prevent infinite re-render loops
  const distributorsKey = useMemo(() => {
    return params.distributors?.slice().sort().join(',') || ''
  }, [params.distributors])

  const fetchAffinity = useCallback(() => {
    if (!orgId || !params.productCode) {
      return
    }

    setIsLoading(true)
    setError(null)

    // Build query string manually to handle array parameters and special characters correctly
    // product_code is a query param to handle codes with slashes (e.g., "AIKO-A455-MAH54Mb/2S")
    const queryParts: string[] = [
      `product_code=${encodeURIComponent(params.productCode)}`,
    ]

    if (params.distributors && params.distributors.length > 0) {
      params.distributors.forEach((dist) => queryParts.push(`distributors=${encodeURIComponent(dist)}`))
    }
    if (params.inStock !== undefined) {
      queryParts.push(`in_stock=${params.inStock}`)
    }
    if (params.limit) {
      queryParts.push(`limit=${params.limit}`)
    }
    if (params.timeframeDays) {
      queryParts.push(`timeframe_days=${params.timeframeDays}`)
    }

    const queryString = `?${queryParts.join('&')}`
    const url = `orgs/${orgId}/product_recommendations/${queryString}`

    restClientInstance('CUSTOM_GET', 'custom', { url })
      .then((response) => {
        if (response?.data) {
          setData(response.data)

          // Track affinity recommendations fetched
          logAmplitudeEvent('shop_product_affinity_fetched', {
            source: 'pdp',
            product_code: params.productCode,
            result_count: response.data.recommended_products?.length || 0,
            cache_hit: response.data.metadata?.cache_hit || false,
          })
        }
      })
      .catch((err: any) => {
        setError(err)
        notify('Failed to fetch product recommendations', 'warning')
        console.error('Error fetching product affinity:', err)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [orgId, params.productCode, distributorsKey, params.inStock, params.limit, params.timeframeDays, notify])

  useEffect(() => {
    fetchAffinity()
  }, [fetchAffinity])

  return { data, isLoading, error, refetch: fetchAffinity }
}
